<script>
import ContentBox from '@/components/ui/ContentBox';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import SwitchDialog from '@/components/billing/SwitchDialog';
import DowngradeDialog from '@/components/billing/DowngradeDialog';
import PaymentInfoDialog from '@/components/billing/PaymentInfoDialog';
import SidebarLink from '@/components/layouts/dashboard/SidebarLink';
import SidebarLinkGroup from '@/components/layouts/dashboard/SidebarLinkGroup';
import UpgradeDialog from '@/components/billing/UpgradeDialog';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'Billing',
  components: {
    ContentBox,
    DashboardLayout,
    SwitchDialog,
    DowngradeDialog,
    PaymentInfoDialog,
    SidebarLink,
    SidebarLinkGroup,
    UpgradeDialog
  },
  metaInfo() {
    return {
      title: this.title
    };
  },
  data() {
    return {
      title: this.$t('Billing'),
      breadcrumbs: [{ text: this.$t('Billing') }],
      account: {},
      customizeInvoicesDialogVisible: false,
      isRenewing: false,
      isSwitching: false,
      payments: [],
      savingPaymentInfo: false,
      savingCustomInvoices: false,
      savingSubscription: false,
      showYearly: this.$store.state.user.account.paymentInterval === 'year',
      statusCode: null
    };
  },
  computed: {
    plan() {
      return this.$store.state.user.account.plan;
    },

    paymentInterval() {
      return this.$store.state.user.account.paymentInterval;
    },

    willCancel() {
      return this.$store.state.user.account.stripeCancelAtPeriodEnd;
    }
  },
  created() {
    // Fetch initial data
    Promise.all([
      // Fetch account info
      this.$http.get('/account')
        .then(res => {
          this.account = res.data.account;
          this.$store.commit('setAccount', this.account);
        }),

      // Fetch payments
      this.$http.get('/account/payments', {
        params: { sortBy: 'invoiceDate' }
      })
        .then(res => this.payments = res.data.payments)
    ])
      .then(() => this.statusCode = 200)
      .catch(err => this.statusCode = err.response.status);
  },
  methods: {
    handleRenew() {
      this.isRenewing = true;
      this.$http.put('/account/subscription', { cancelAtPeriodEnd: false })
        .then(res => {
          this.account = res.data.account;
          this.$store.commit('setAccount', this.account);
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: 'Error',
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => this.isRenewing = false);
    },

    // Handles the customize invoices form
    saveCustomizeInvoices() {
      this.savingCustomInvoices = true;
      this.$http.put('/account', {
        organization: this.account.organization,
        invoiceText: this.account.invoiceText
      })
        .then(res => {
          this.account = res.data.account;
          this.$store.commit('setAccount', this.account);
          this.customizeInvoicesDialogVisible = false;
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => this.savingCustomInvoices = false);
    }
  }
};
</script>

<template>
  <dashboard-layout :status-code="statusCode" size="medium">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Sidebar -->
    <template slot="sidebar">
      <!-- Actions -->
      <sidebar-link-group :title="$t('Actions')">
        <sidebar-link
          v-if="$store.state.user.canManageSettings"
          :text="$t('Customize Invoices')"
          icon="el-icon-fa-file-invoice-dollar"
          @click.native.prevent="customizeInvoicesDialogVisible = true"
        />
        <sidebar-link
          v-if="['business', 'agency'].includes(plan)"
          :text="$t('Update Payment Info')"
          icon="el-icon-fa-credit-card"
          @click="$refs.paymentInfoDialog.show()"
        />
      </sidebar-link-group>
    </template>

    <!-- My account -->
    <div class="my-account">
      <h2>{{ $t('My Account') }}</h2>

      <!--
        User is NOT subscribed.

        NOTE: This section intentionally not translated.
      -->
      <div v-if="plan === 'personal'" class="plans">
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12" :md="12" :lg="8" :xl="8">
            <div class="plan is-selected">
              <div class="plan-header plan-personal">
                <h3>Personal</h3>
              </div>
              <div class="plan-body">
                <div class="plan-price">
                  Free <small>forever</small>
                </div>
                <ul class="plan-features">
                  <li>Unlimited sites</li>
                  <li>Visual editing</li>
                  <li>Email support</li>
                  <li><del>Commercial use</del></li>
                  <li><del>Analytics dashboard</del></li>
                  <li><del>Change notifications</del></li>
                  <li><del>White-label features</del></li>
                  <li><del>Developer API</del></li>
                </ul>
                <p class="text-center">
                  <small>
                    For personal, educational, and non-profit websites.
                  </small>
                </p>
                <div class="plan-footer">
                  <el-button
                    :disabled="true"
                    type="warning"
                    @click="$refs.downgradeDialog.show()"
                  >
                    Your Plan
                  </el-button>
                </div>
              </div>
            </div>
          </el-col>
          <el-col :xs="24" :sm="12" :md="12" :lg="8" :xl="8">
            <div class="plan">
              <div class="plan-header plan-business">
                <h3>Business</h3>
              </div>
              <div class="plan-body">
                <div class="plan-price">
                  <span v-if="!showYearly"><span class="plan-unit">$</span>12 <small>monthly</small></span>
                  <span v-if="showYearly"><span class="plan-unit">$</span>120 <small>yearly</small></span>
                </div>
                <ul class="plan-features">
                  <li>Unlimited sites</li>
                  <li>Visual editing</li>
                  <li>Priority support</li>
                  <li>Commercial use</li>
                  <li>Analytics dashboard</li>
                  <li><del>Change notifications</del></li>
                  <li><del>White-label features</del></li>
                  <li><del>Developer API</del></li>
                </ul>
                <p class="text-center">
                  <small>
                    For accounts managing commercial websites.
                  </small>
                </p>
                <div class="plan-footer">
                  <el-button
                    type="success"
                    @click="$refs.upgradeDialog.show({ plan: 'business', paymentInterval: showYearly ? 'year' : 'month' })"
                  >
                    Upgrade
                  </el-button>
                </div>
              </div>
            </div>
          </el-col>
          <el-col :xs="24" :sm="12" :md="12" :lg="8" :xl="8">
            <div class="plan">
              <div class="plan-header plan-agency">
                <h3>Agency</h3>
              </div>
              <div class="plan-body">
                <div class="plan-price">
                  <span v-if="!showYearly"><span class="plan-unit">$</span>24 <small>monthly</small></span>
                  <span v-if="showYearly"><span class="plan-unit">$</span>240 <small>yearly</small></span>
                </div>
                <ul class="plan-features">
                  <li>Unlimited sites</li>
                  <li>Visual editing</li>
                  <li>Priority support</li>
                  <li>Commercial use</li>
                  <li>Analytics dashboard</li>
                  <li>Change notifications</li>
                  <li>White-label features</li>
                  <li>Developer API <small>(coming soon)</small></li>
                </ul>
                <p class="text-center">
                  <small>
                    For agencies that want a white-label solution.
                  </small>
                </p>
                <div class="plan-footer">
                  <el-button
                    type="primary"
                    @click="$refs.upgradeDialog.show({ plan: 'agency', paymentInterval: showYearly ? 'year' : 'month' })"
                  >
                    Upgrade
                  </el-button>
                </div>
              </div>
            </div>
          </el-col>
        </el-row>

        <div class="text-center">
          <el-switch
            v-model="showYearly"
            inactive-text="Monthly"
            active-text="Yearly"
            active-color="#a6adb2"
            inactive-color="#a6adb2"
          />
        </div>
      </div>

      <!--
        User is subscribed

        NOTE: This section intentionally not translated.
      -->
      <content-box
        v-if="plan !== 'personal' && !willCancel"
        class="subscribed"
      >
        <div class="text-center">
          <h2>Thanks for subscribing to Surreal&nbsp;CMS!</h2>
          <p>
            You are subscribed to <a href="https://www.surrealcms.com/pricing.html" target="_blank">our {{ plan }} plan</a>.
          </p>
        </div>
        <div class="primary-image bounce">
          <img src="@/assets/rocket.svg" alt="Open box">
        </div>

        <div class="text-center">
          <p class="payments-will-be-charged">
            <!-- A credit card is on file -->
            <span v-if="$store.state.user.account.stripeCardBrand">
              Payments will be billed once a {{ paymentInterval }}
              to your <a href="#" @click.prevent="$refs.paymentInfoDialog.show()">{{ $store.state.user.account.stripeCardBrand }}
              ending in {{ $store.state.user.account.stripeCardLast4 }}</a>.
            </span>
            <!-- A credit card is not on file -->
            <span v-else>
              Please add a credit card for future payments.
            </span>
          </p>

          <!-- Switch plan -->
          <el-button
            :loading="isSwitching"
            type="primary"
            @click="$refs.switchDialog.show({ plan, paymentInterval })"
          >
            Switch Plan
          </el-button>

          <!-- Cancel subscription -->
          <el-button type="text" @click="$refs.downgradeDialog.show()">
            Cancel Subscription
          </el-button>
        </div>
      </content-box>

      <!--
        Subscription will cancel

        NOTE: This section intentionally not translated.
      -->
      <content-box
        v-if="$store.state.user.account.plan !== 'personal' && willCancel"
        class="subscription-will-cancel"
      >
        <div class="text-center">
          <h2>Your subscription is ending</h2>
          <p>
            Your subscription is schedule to end on
            {{ $d(new Date($store.state.user.account.stripeCurrentPeriodEnd), 'short') }}.
          </p>
        </div>
        <div class="primary-image bounce">
          <img src="@/assets/balloons.svg" alt="A red and a blue balloon">
        </div>
        <div class="text-center">
          <p>
            You will lose access to premium features and you will only be allowed to manage websites
            for personal, educational, and non-profit use. Accounts are actively audited to prevent
            abuse.
          </p>
          <p>
            Change your mind? We’d love to have you back!
          </p>
          <el-button :loading="isRenewing" type="primary" @click="handleRenew">
            Do Not Cancel
          </el-button>
        </div>
      </content-box>
    </div>

    <!-- Payment history -->
    <div v-if="payments.length" class="payment-history">
      <h2>{{ $t('Payment History') }}</h2>
      <content-box>
        <table class="table table-hover">
          <thead>
            <tr>
              <th />
              <th>{{ $t('Date') }}</th>
              <th class="hidden-sm-and-down">{{ $t('Payment Source') }}</th>
              <th>{{ $t('Amount') }}</th>
              <th class="text-center">{{ $t('Invoice') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="payment in payments" :key="payment.id">
              <td><i class="el-icon-fa-check text-success" /></td>
              <td>{{ $d(new Date(payment.invoiceDate), 'short') }}</td>

              <td class="hidden-sm-and-down">
                {{
                  $t('{brand} ending in {last4}', {
                    brand: payment.stripeCardBrand,
                    last4: payment.stripeCardLast4
                  })
                }}
              </td>

              <td>{{ $n(payment.amount / 100, 'currency') }}</td>
              <td class="text-center">
                <a
                  :href="`/billing/invoices/${payment.id}`"
                  :title="$t('View Invoice')"
                  target="_blank"
                >
                  <i class="el-icon-fa-external-link" />
                </a>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="$store.state.user.canManageSettings" class="text-form-description">
          {{ $t('Need something else to appear on your invoice?') }}
          <a href="#" @click.prevent="customizeInvoicesDialogVisible = true">
            {{ $t('Customize Invoices') }}
          </a>
        </div>
      </content-box>
    </div>

    <!-- Dialogs -->
    <payment-info-dialog ref="paymentInfoDialog" />
    <upgrade-dialog ref="upgradeDialog" />
    <switch-dialog ref="switchDialog" />
    <downgrade-dialog ref="downgradeDialog" />

    <!-- Customize invoices dialog -->
    <el-dialog
      v-if="$store.state.user.canManageSettings"
      :title="$t('Customize Invoices')"
      :visible.sync="customizeInvoicesDialogVisible"
    >
      <el-form @submit.native.prevent="saveCustomizeInvoices">
        <!-- Organization -->
        <el-form-item :label="$t('Organization')">
          <el-input
            v-model="account.organization"
            type="text"
            auto-complete="off"
          />
        </el-form-item>

        <!-- Additional text -->
        <el-form-item :label="$t('Additional Text')">
          <el-input
            v-model="account.invoiceText"
            :rows="3"
            type="textarea"
            auto-complete="off"
          />
          <div class="text-form-description">
            {{ $t('This information will appear on all of your invoices.') }}
          </div>
        </el-form-item>

        <!-- Allow the form to submit when enter is pressed inside a field -->
        <el-button v-show="false" native-type="submit" />
      </el-form>

      <!-- Footer -->
      <span slot="footer" class="dialog-footer">
        <el-button @click="customizeInvoicesDialogVisible = false">
          {{ $t('Cancel') }}
        </el-button>
        <el-button :loading="savingCustomInvoices" type="primary" @click="saveCustomizeInvoices">
          {{ $t('Submit') }}
        </el-button>
      </span>
    </el-dialog>
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

@keyframes bounce {
  0% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(0, -.25rem, 0);
  }
  100% {
    transform: translate3d(0, 0, 0);
  }
}

.my-account {
  margin-bottom: 2rem;
}

.plans {
  margin-bottom: 2rem;

  .plan {
    background: $--color-white;
    border-radius: .25rem;
    box-shadow: $--box-shadow-small;
    margin-bottom: 1rem;

    .plan-header {
      background-color: $--color-gray-50;
      border-top-left-radius: .25rem;
      border-top-right-radius: .25rem;
      text-align: center;
      color: $--color-white;
      padding: 3rem 1.5rem;

      &.plan-personal {
        background-color: $--color-warning;
      }

      &.plan-business {
        background-color: $--color-success;
      }

      &.plan-agency {
        background-color: $--color-primary;
      }

      h3 {
        font-weight: bold;
        font-size: 1.8rem;
        color: inherit;
        text-transform: uppercase;
        margin-bottom: .5rem;
      }
    }

    .plan-body {
      line-height: 1.4;
      padding: 1.5rem;

      .plan-price {
        font-weight: 500;
        font-size: 3.4rem;
        text-align: center;
        margin-bottom: 1.5rem;

        small {
          color: $--color-gray-40;
          display: block;
          margin-top: -.25rem;
        }
      }

      .plan-unit {
        display: inline-block;
        font-size: 1.6rem;
        vertical-align: text-top;
        margin-top: .45rem;
      }

      p {
        line-height: inherit;
      }

      .plan-features {
        list-style: none;
        padding: 0;
        margin: 0 0 1.5rem 0;

        li {
          text-align: center;
          padding: 0;
          margin: 0 0 .4rem 0;
        }

        del {
          color: $--color-gray-40;
        }
      }
    }

    .plan-footer {
      display: flex;
      justify-content: space-between;
      border-top: solid 1px $--color-gray-10;
      padding-top: 1.5rem;

      .el-button {
        display: block;
        width: 100%;

        small {
          font-size: .75em;
        }
      }

      .el-button {
        width: auto;
        flex: 1 1 auto;
      }
    }
  }
}

.payments-will-be-charged {
  margin-bottom: 2rem;
}

// Subscribed view
.subscribed,
.subscription-will-cancel {
  h2 {
    font-size: 1.8rem;
    margin-bottom: 0;
  }

  .primary-image {
    img {
      display: block;
      max-width: 16rem;
      margin: 1.5rem auto;
    }
  }

  .bounce {
    animation: 2.25s bounce;
    animation-iteration-count: infinite;
  }
}

.payment-history {
  .table {
    margin-bottom: 1.5rem;
  }
}
</style>
